import { flattenProp, withProps } from 'recompose'
import moment from 'moment'

import FeatureBlock from '@components/FeatureBlock'

import { locationWithBrandSelector } from 'car-parks/selectors'
import AvailabilityLegendItem from './AvailabilityLegendItem'
import { OCCUPANCY_STATUSES } from '../constants'
import BuyButtonCTA from 'map/components/actionButton/BuyButton'
import * as selectors from 'map/selectors'

const Content = ({
    name,
    brandLogo,
    brand,
    shortDescription,
    isOpen247,
    openingHours,
    openingHoursPerWeekday,
    totalSpaces,
    phone,
    opngo,
    externalSeasonTicketUrl,
    buyLabel,
    canBuy,
    bookingUrl,
    email,
    longDescription,
    siteStatus,
    freeSpaces,
    address,
    garageIsUnderground,
    mscpGarageIsAboveGround,
    isOnSurface,
    womenOnly,
    heightRestriction,
    stayRestrictions,
    what3Words,
}) => {
    const allWeekDaysHaveOpeningHours = !openingHoursPerWeekday.some(val => !val)

    const combineAddress = address => {
        if (address) {
            const { lines = [], zipCode = '', city = '', state = '' } = address
            return `${lines.join(',\n')}${lines.length > 0 ? ',\n' : ''}${city}, \n${state}, \n${zipCode}.`
        }
        return ''
    }
    const combinedAddress = React.useMemo(() => combineAddress(address), [address])

    return (
        <div className="container main">
            <div className="details">
                <h1 className="text-center">
                    {name}
                    {brandLogo && (
                        <span className="brand-logo">
                            <Image image={brandLogo} alt={brand} />
                        </span>
                    )}
                </h1>
                <div className="feature-section">
                    {(isOpen247 || openingHours || allWeekDaysHaveOpeningHours) && (
                        <FeatureBlock
                            label={<T>label.park.opening.hours</T>}
                            icon="clock"
                            details={isOpen247 && <T>label.park.247</T>}>
                            {!isOpen247 && allWeekDaysHaveOpeningHours
                                ? openingHoursPerWeekday.map((time, index) => (
                                      <div key={index} className="opening-hours-day">
                                          <span className="opening-hours-day-abbr">
                                              <T>{moment.weekdaysShort(index + 1)}</T>
                                          </span>
                                          <span className="opening-hours-day-time">{time}</span>
                                      </div>
                                  ))
                                : openingHours && openingHours}
                        </FeatureBlock>
                    )}
                    {totalSpaces && (
                        <FeatureBlock
                            label={<T>{freeSpaces ? 'label.park.spaces.free' : 'label.park.spaces.total'}</T>}
                            icon="park"
                            details={`${freeSpaces ? `${freeSpaces}/` : ''}${totalSpaces}`}>
                            {!!siteStatus && !freeSpaces && (
                                <div className="avaliability-current">
                                    <AvailabilityLegendItem
                                        color={siteStatus.toLowerCase()}
                                        text={OCCUPANCY_STATUSES[siteStatus.toLowerCase()].label}
                                    />
                                </div>
                            )}
                        </FeatureBlock>
                    )}
                    {mscpGarageIsAboveGround && <FeatureBlock label={<T>label.park.mscp</T>} icon="multistore" />}
                    {isOnSurface && <FeatureBlock label={<T>label.park.onsurface</T>} icon="outside" />}
                    {!!heightRestriction && (
                        <FeatureBlock
                            label={<T>label.park.max.height</T>}
                            icon="height"
                            details={`${heightRestriction}m`}
                        />
                    )}
                    {garageIsUnderground && <FeatureBlock label={<T>label.park.underground</T>} icon="indoor" />}
                    {womenOnly && <FeatureBlock label={<T>label.park.women.only</T>} icon="woman" />}
                    {stayRestrictions && (
                        <FeatureBlock
                            label={<T>label.park.stay.restriction</T>}
                            icon="restriction"
                            details={stayRestrictions}
                        />
                    )}
                </div>
                <div className="row no-gutters">
                    {shortDescription && (
                        <div className="col-lg-4">
                            <p>{shortDescription}</p>
                        </div>
                    )}
                    <div className="col-lg-6 col-md-8">
                        {address && (
                            <FeatureBlock
                                size="sm"
                                icon="address"
                                className="car-park-address"
                                label={combinedAddress}
                            />
                        )}
                        {phone && <FeatureBlock size="sm" label={<a href={`tel:${phone}`}>{phone}</a>} icon="phone" />}
                        {email && (
                            <FeatureBlock size="sm" label={<a href={`mailto:${email}`}>{email}</a>} icon="email" />
                        )}
                        {what3Words && (
                            <FeatureBlock
                                size="sm"
                                label={
                                    <a
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        href={`https://what3words.com/${what3Words}`}>
                                        {what3Words}
                                    </a>
                                }
                                icon="what-3-words"
                            />
                        )}
                        {opngo && (
                            <div className="mt-5">
                                <BuyButtonCTA
                                    canBuy={canBuy}
                                    bookingUrl={bookingUrl}
                                    buyLabel={buyLabel}
                                    opngo={opngo}
                                    externalSeasonTicketUrl={externalSeasonTicketUrl}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="description lead" dangerouslySetInnerHTML={{ __html: longDescription }} />
        </div>
    )
}

export default compose(
    connect(
        createStructuredSelector({
            locationWithBrand: locationWithBrandSelector,
            buyLabel: selectors.buyLabelTranslationSelector,
            bookingUrl: selectors.bookingUrlSelector,
            canBuy: selectors.canBuySelector,
        })
    ),
    withProps(
        ({
            openingHoursMonday,
            openingHoursTuesday,
            openingHoursWednesday,
            openingHoursThursday,
            openingHoursFriday,
            openingHoursSaturday,
            openingHoursSunday,
        }) => ({
            openingHoursPerWeekday: [
                openingHoursMonday,
                openingHoursTuesday,
                openingHoursWednesday,
                openingHoursThursday,
                openingHoursFriday,
                openingHoursSaturday,
                openingHoursSunday,
            ],
        })
    ),
    flattenProp('locationWithBrand')
)(Content)
